import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';

import type { FormSubmitEventTarget } from '@/types/dom';
import { DropzoneInput } from '@/components/subcomponents/shared/dropzone';

import vcard from ':assets/images/vcard.png';
import pdf from ':assets/images/pdf.png';

import { Modal } from '../../../shared/Modal';
import { TicketFileUploaderContext } from '../ticket-file-uploader.context';

export interface OutboundMmsFormProps {
  ticket: string;
  handleSubmit: (
    event: React.FormEvent<HTMLFormElement>&{ target: FormSubmitEventTarget }
  ) => void;
  embeddedVarsEnabled: boolean;
  textArea: React.RefObject<HTMLTextAreaElement & {
    nextSibling: {
      CodeMirror: {
        setValue: (value:string) => void;
        on(event: string, listener: Function): void;
        off(event: string, listener: Function): void;
        getValue: () => void;
        focus: () => void;
      }
    }
  }>
}

const mediaSourceFromUrl = (url: string) => {
  const ext = url.substr(-4);
  if (ext === '.pdf') return pdf;
  if (ext === '.vcf') return vcard;
  return url;
};

export const OutboundMmsForm = React.memo(
  ({ handleSubmit, embeddedVarsEnabled, textArea }: OutboundMmsFormProps) => {
    const [active, setActive] = useState(false);
    const clearChatbox = useCallback(() => {
      if (embeddedVarsEnabled) {
        const cmInstance = textArea.current.nextSibling.CodeMirror;
        cmInstance?.setValue('');
      }
      const textAreaCurrent = textArea.current;
      textAreaCurrent.value = '';
    }, [embeddedVarsEnabled, textArea]);

    const mmsTextAreaRef = useRef<HTMLTextAreaElement>();
    const [mediaUrl, setMediaUrl] = useState('');
    const [mediaDimensions, setMediaDimensions] = useState('');
    const [mediaType, setMediaType] = useState('');
    const clearMmsForm = () => {
      mmsTextAreaRef.current.value = '';
      setMediaUrl('');
      setMediaDimensions('');
      setMediaType('');
    };
    const handleFormSubmit = useCallback<typeof handleSubmit>((event) => {
      event.preventDefault();
      handleSubmit(event);
      setActive(false);
      clearChatbox();
      clearMmsForm();
    }, [handleSubmit, setActive, clearChatbox]);
    const handleCancel = useCallback(() => {
      setActive(false);
      clearMmsForm();
    }, [setActive]);

    const urlInputRef = useRef<HTMLInputElement>();
    const handleMediaUrlUpdate = () => {
      const { value } = urlInputRef.current;
      if (value !== mediaUrl) setMediaUrl(value);
      setActive(true);
    };

    const fileUploader = useContext(TicketFileUploaderContext);
    useEffect(() => {
      if (!fileUploader) return undefined;
      const listener: Parameters<typeof fileUploader['addUploadCompleteListener']>[0] = (event) => {
        const [{ url, dimensions, type }] = event.detail;
        setMediaUrl(url);
        setMediaType(type);
        if (dimensions) {
          const { width, height } = dimensions;
          setMediaDimensions(JSON.stringify({ w: width, h: height }));
        }
        handleMediaUrlUpdate();
      };
      fileUploader?.addUploadCompleteListener(listener);

      return () => fileUploader?.removeUploadCompleteListener(listener);
    }, [fileUploader]);

    if (!fileUploader) return null;

    return (
      <Modal active={active} showDarkOverlay>
        <div className="ui-popup-container ui-popup-active" id="outbound-mms-modal-popup">
          <div className="ui-content ui-popup ui-body-inherit ui-overlay-shadow ui-corner-all" id="outbound-mms-modal">
            <h1>Send this MMS</h1>

            <DropzoneInput className="hide" />

            <form id="outbound-mms" acceptCharset="UTF-8" onSubmit={handleFormSubmit}>
              <div className="media-container">
                <img
                  id="media-message"
                  className="mms-media"
                  alt="mms media"
                  src={mediaSourceFromUrl(mediaUrl)}
                />
              </div>

              <input type="hidden" name="scheduled_for" id="scheduled_for" className="scheduled-for-input" />
              <input ref={urlInputRef} type="hidden" name="media_url" id="media_url" value={mediaUrl} onClick={handleMediaUrlUpdate} />
              <input type="hidden" name="media_type" id="media_type" value={mediaType} />
              <input type="hidden" name="media_dimensions" value={mediaDimensions} />
              <textarea
                ref={mmsTextAreaRef}
                name="message"
                id="message"
                placeholder="Add a message here (optional)"
                className="ui-input-text ui-shadow-inset ui-body-inherit ui-corner-all ui-textinput-autogrow"
                style={{ height: 52 }}
              />

              <div className="desc">
                <button type="button" className="media_closer button-cancel" data-role="none" onClick={handleCancel}>Cancel</button>
                <input type="submit" name="send" value="Send" className="button-submit" data-role="none" />
              </div>
            </form>
          </div>
        </div>
      </Modal>
    );
  },
);
